import { datadogRum, DEFAULT_TRACKED_RESOURCE_HEADERS, RumEvent } from '@datadog/browser-rum'
import { vuePlugin } from '@datadog/browser-rum-vue'

import {
  AppLocation,
  isDatadogConfigured,
  normalizeLocation,
  objEntries,
  objFromEntries,
  truthy,
} from '@lasso/shared/utils'

import { AppConfig } from '@root-config/types'
import { isAppActive } from '@root-config/utils'

const sensitiveKey = /authorization|cookie|password/i

const scrubSensitive = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(scrubSensitive)
  }

  if (value !== null && typeof value === 'object') {
    return objFromEntries(
      objEntries(value as Record<string, unknown>).map(([key, nested]) => [
        key,
        sensitiveKey.test(key) ? '[REDACTED]' : scrubSensitive(nested),
      ]),
    )
  }

  return value
}

const ignoredErrorMessage = /resizeobserver loop/i

const scrubEventContext = (event: RumEvent): boolean => {
  if (event.type === 'error' && ignoredErrorMessage.test(event.error.message)) {
    return false
  }

  if (event.context) {
    event.context = scrubSensitive(event.context) as RumEvent['context']
  }

  return true
}

const dynamicUrlSegment = /^(?:\d+|[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12})$/i

/**
 * Builds a stable, parameterized RUM view name from a single-spa location by
 * replacing dynamic path segments (numeric ids, uuids) with `:id`.
 *
 * @example
 * app/activation/campaign/123 -> /app/activation/campaign/:id
 */
const getViewName = (location: AppLocation): string => {
  const { pathname } = normalizeLocation(location)

  if (pathname === '/') {
    return '/'
  }

  const normalizedPath = pathname
    .split('/')
    .map((segment) => (dynamicUrlSegment.test(segment) ? ':id' : segment))
    .join('/')

  return `/${normalizedPath}`
}

const getActiveApp = (apps: Array<AppConfig | null>, location: AppLocation): AppConfig | undefined => {
  return apps.filter(truthy).find((app) => !app.isAside && isAppActive(app.activeWhen, location))
}

export const initDatadog = (): void => {
  if (!isDatadogConfigured()) {
    return
  }

  datadogRum.init({
    applicationId: process.env.PUBLIC_DATADOG_APPLICATION_ID!,
    clientToken: process.env.PUBLIC_DATADOG_CLIENT_TOKEN!,
    site: process.env.PUBLIC_DATADOG_SITE || 'datadoghq.com',
    service: 'imos-frontend',
    env: process.env.PUBLIC_DEPLOYMENT_ENVIRONMENT || undefined,
    version: process.env.PUBLIC_DEPLOYMENT_VERSION || undefined,
    sessionSampleRate: Number.parseInt(process.env.PUBLIC_DATADOG_SESSION_SAMPLE_RATE ?? '', 10) || 0,
    sessionReplaySampleRate: Number.parseInt(process.env.PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE ?? '', 10) || 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackViewsManually: true,
    actionNameAttribute: 'data-analytics-id',
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: scrubEventContext,
    plugins: [vuePlugin()],
    allowedTracingUrls: [process.env.PUBLIC_ACT_API_URL, process.env.PUBLIC_API_URL].filter(truthy),
    trackResourceHeaders: [
      ...DEFAULT_TRACKED_RESOURCE_HEADERS.map((name) => ({ name })),
      { name: 'x-request-id' },
      { name: 'x-correlation-id' },
    ],
  })
  datadogRum.setGlobalContextProperty('bu', 'iqd')
  datadogRum.setGlobalContextProperty('product', 'mediaos')
}

export const startDatadogViewTracking = (apps: Array<AppConfig | null>): void => {
  if (!isDatadogConfigured()) {
    return
  }

  let lastViewName: string | null = null

  const updateView = (): void => {
    const location = normalizeLocation(window.location)
    const viewName = getViewName(location)

    if (viewName === lastViewName) {
      return
    }

    lastViewName = viewName

    datadogRum.startView({
      name: viewName,
      context: {
        microfrontend: getActiveApp(apps, location)?.id ?? null,
      },
    })
  }

  window.addEventListener('single-spa:routing-event', updateView)
  updateView()
}
